/**
 * Mo Woodwork — WebMCP integration
 * Exposes key shop actions to AI browser agents (Chrome WebMCP, Cloudflare Agent SDK, etc.)
 * Spec: https://webmachinelearning.github.io/webmcp/
 *
 * Tools exposed:
 *   - search_products(query)       → keyword search via /search/suggest.json
 *   - get_product_details(handle)  → variants + options via /products/{handle}.js
 *   - add_to_cart(variant_id, qty) → POST /cart/add.js
 *   - view_cart()                  → GET /cart.js
 *
 * Safe-by-default: bails out silently when navigator.modelContext is absent.
 * Honors Shopify multi-market routing via window.Shopify.routes.root.
 */
(function () {
  'use strict';

  if (typeof navigator === 'undefined' || !navigator.modelContext) {
    return;
  }

  var ctx = navigator.modelContext;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function errorEnvelope(message, extra) {
    var out = { success: false, error: String(message || 'Unknown error') };
    if (extra && typeof extra === 'object') {
      for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
    }
    return out;
  }

  async function fetchJsonOrError(url, options) {
    var res;
    try {
      res = await fetch(url, options || { credentials: 'same-origin' });
    } catch (networkErr) {
      return { __error: errorEnvelope(networkErr && networkErr.message || 'Network error') };
    }
    var data = null;
    try { data = await res.json(); } catch (_) { /* keep null */ }
    if (!res.ok) {
      var msg = (data && (data.description || data.message)) || res.statusText || 'Request failed';
      return { __error: errorEnvelope(msg, { status: res.status }) };
    }
    return { __data: data };
  }

  function trimHandle(input) {
    var s = String(input || '').trim();
    var m = s.match(/(?:^|\/products\/)([^\/\?#]+)/);
    return m ? m[1] : s.replace(/^\/+|\/+$/g, '');
  }

  function mapVariant(v) {
    return {
      id: v.id,
      title: v.title,
      price: v.price,
      available: v.available,
      options: v.options,
      sku: v.sku || null
    };
  }

  function mapLineItem(item) {
    return {
      title: item.title,
      quantity: item.quantity,
      price: item.price,
      line_price: item.line_price,
      variant_id: item.variant_id || item.id,
      url: item.url || null
    };
  }

  var tools = [
    {
      name: 'search_products',
      description: 'Search the Mo Woodwork catalog of handcrafted wooden furniture (record player cabinets, dining tables, shelves, desks, DJ booths). Returns up to 10 matching products with title, price, URL, availability, and image.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          query: {
            type: 'string',
            minLength: 1,
            maxLength: 120,
            description: 'Keyword(s) to search for, e.g. "record player stand", "walnut dining table", "oak desk with hutch".'
          }
        },
        required: ['query']
      },
      execute: async function (input) {
        var q = String((input && input.query) || '').trim();
        if (!q) return errorEnvelope('query must be a non-empty string');
        var url = ROOT + 'search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
        var result = await fetchJsonOrError(url);
        if (result.__error) return result.__error;
        var data = result.__data;
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        return {
          success: true,
          query: q,
          count: products.length,
          products: products.map(function (p) {
            return {
              title: p.title,
              handle: p.handle,
              url: p.url,
              price: p.price,
              compare_at_price: p.compare_at_price_min || null,
              available: p.available,
              vendor: p.vendor,
              type: p.type,
              image: p.image || (p.featured_image && p.featured_image.url) || null
            };
          })
        };
      }
    },
    {
      name: 'get_product_details',
      description: 'Fetch full details of a Mo Woodwork product by its handle (URL slug). Returns description, variants with their IDs (required for add_to_cart), options (e.g. wood finish, size), price range, images, and availability.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          handle: {
            type: 'string',
            minLength: 1,
            maxLength: 200,
            description: 'Product handle (URL slug) from search_products.products[].handle. Example: "tonn-101-record-player-table-shelf-of-records-solid-walnut". Full product URLs are also accepted.'
          }
        },
        required: ['handle']
      },
      execute: async function (input) {
        var handle = trimHandle(input && input.handle);
        if (!handle) return errorEnvelope('handle must be a non-empty string');
        var url = ROOT + 'products/' + encodeURIComponent(handle) + '.js';
        var result = await fetchJsonOrError(url);
        if (result.__error) return result.__error;
        var data = result.__data;
        return {
          success: true,
          id: data.id,
          title: data.title,
          handle: data.handle,
          url: data.url,
          description: data.description,
          vendor: data.vendor,
          type: data.type,
          tags: data.tags || [],
          price: data.price,
          price_min: data.price_min,
          price_max: data.price_max,
          compare_at_price: data.compare_at_price || null,
          available: data.available,
          options: data.options || [],
          variants: (data.variants || []).map(mapVariant),
          featured_image: data.featured_image || null,
          images: (data.images || []).slice(0, 6)
        };
      }
    },
    {
      name: 'add_to_cart',
      description: 'Add a specific product variant to the shopping cart. Obtain variant_id from get_product_details.variants[].id. Returns the updated line item. Does not redirect. The agent SHOULD obtain explicit user confirmation before calling this tool, as it mutates the user\'s cart. The agent can call view_cart afterwards to confirm.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          variant_id: {
            type: 'integer',
            minimum: 1,
            description: 'Shopify variant ID from get_product_details.variants[].id. NOT the product ID.'
          },
          quantity: {
            type: 'integer',
            minimum: 1,
            maximum: 100,
            default: 1,
            description: 'How many units to add (default 1).'
          }
        },
        required: ['variant_id']
      },
      execute: async function (input) {
        var variantId = parseInt((input && input.variant_id), 10);
        var qty = parseInt((input && input.quantity), 10);
        if (!variantId || isNaN(variantId) || variantId < 1) {
          return errorEnvelope('variant_id must be a positive integer');
        }
        if (!qty || isNaN(qty) || qty < 1) qty = 1;
        if (qty > 100) qty = 100;

        var result = await fetchJsonOrError(ROOT + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
        });
        if (result.__error) return result.__error;
        var data = result.__data;

        var items = (data && data.items) ? data.items : (data ? [data] : []);
        var mapped = items.map(mapLineItem);

        // Notify theme cart UI so drawer/count can refresh
        try {
          if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
            document.dispatchEvent(new CustomEvent('webmcp:cart-updated', {
              detail: { added: mapped, source: 'webmcp' }
            }));
          }
        } catch (_) { /* non-fatal */ }

        return { success: true, added: mapped };
      }
    },
    {
      name: 'view_cart',
      description: 'View current shopping cart contents — all line items, total price, currency, and item count. Call this after add_to_cart to confirm, or any time to inspect the cart.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {},
        required: []
      },
      execute: async function () {
        var result = await fetchJsonOrError(ROOT + 'cart.js');
        if (result.__error) return result.__error;
        var data = result.__data;
        return {
          success: true,
          item_count: data.item_count,
          total_price: data.total_price,
          original_total_price: data.original_total_price,
          total_discount: data.total_discount,
          currency: data.currency,
          items: (data.items || []).map(mapLineItem)
        };
      }
    }
  ];

  var registered = 0;

  // W3C spec imperative API: registerTool(tool) per tool
  if (typeof ctx.registerTool === 'function') {
    tools.forEach(function (tool) {
      try {
        ctx.registerTool(tool);
        registered++;
      } catch (e) {
        if (window.console && console.warn) console.warn('[WebMCP] registerTool failed for ' + tool.name, e);
      }
    });
  }

  // Alternative proposed API used by some scanners (Cloudflare isitagentready.com)
  if (typeof ctx.provideContext === 'function') {
    try {
      ctx.provideContext({ tools: tools });
      if (!registered) registered = tools.length;
    } catch (e) {
      if (window.console && console.warn) console.warn('[WebMCP] provideContext failed', e);
    }
  }
})();
